import { useEffect, useState } from "react";

/**
 * Sticky offline banner — mounted once at the app root, listens to
 * `online` / `offline` window events, animates in / out.
 *
 * When offline: high-z fixed banner at the top of the viewport with a
 * wifi-off icon and "You're offline" copy. Polite aria-live so AT
 * users are told without yanking focus.
 *
 * When back online: banner stays visible for one more second as a
 * "You're back" confirmation, then retreats. Spares the user the
 * "did it actually reconnect or not?" moment.
 */
export function OfflineBanner() {
    const [state, setState] = useState<"online" | "offline" | "recovered">(() =>
        typeof navigator !== "undefined" && navigator.onLine === false
            ? "offline"
            : "online",
    );

    useEffect(() => {
        const onOnline = () => {
            setState("recovered");
            const t = setTimeout(() => setState("online"), 1600);
            return () => clearTimeout(t);
        };
        const onOffline = () => setState("offline");
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        return () => {
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);
        };
    }, []);

    if (state === "online") return null;

    const isRecovered = state === "recovered";
    return (
        <div
            className={`offline-banner ${isRecovered ? "offline-banner--recovered" : ""}`}
            role="status"
            aria-live="polite"
        >
            <span className="offline-dot" aria-hidden="true" />
            <span>
                {isRecovered ? "You're back online." : "You're offline. Changes will not be saved until you reconnect."}
            </span>
        </div>
    );
}
